import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, onForcedLogout, tokens, type AuthUser } from './api';

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<AuthUser>;
  signOut: () => Promise<void>;
  can: (permission: string) => boolean;
  isPlatform: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

function grantMatches(grant: string, required: string): boolean {
  if (grant === '*' || grant === required) return true;
  const [grantResource, grantAction] = grant.split(':');
  const [needResource, needAction] = required.split(':');
  return (
    (grantResource === '*' || grantResource === needResource) &&
    (grantAction === '*' || grantAction === needAction)
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => tokens.user());
  const [loading, setLoading] = useState(true);

  // Re-validate the stored session on boot. A revoked user or a suspended
  // client must not keep a usable-looking UI from localStorage.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!tokens.access()) {
        setLoading(false);
        return;
      }
      try {
        const fresh = await api.get<AuthUser>('/auth/me');
        if (!cancelled) setUser(fresh);
      } catch {
        if (!cancelled) {
          tokens.clear();
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The API client signals a forced logout when a refresh fails.
  useEffect(() => onForcedLogout(() => setUser(null)), []);

  const signIn = useCallback(async (email: string, password: string) => {
    const next = await api.login(email, password);
    setUser(next);
    return next;
  }, []);

  const signOut = useCallback(async () => {
    await api.logout();
    setUser(null);
  }, []);

  const can = useCallback(
    (permission: string) =>
      (user?.permissions ?? []).some((g) => grantMatches(g, permission)),
    [user],
  );

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      signIn,
      signOut,
      can,
      isPlatform: user?.actor === 'platform',
    }),
    [user, loading, signIn, signOut, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
